import {
  keepPreviousData,
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type {
  CreateSchedulerTeamRequest,
  CreateWorkSessionRequest,
  CreateWorkSessionsRequest,
  CreateWorkSessionsResponse,
  CreateWorkerLeaveRequestInput,
  CreateWorkerRequest,
  EnrollSchedulerProjectRequest,
  PreviewSchedulerEnrollmentRequest,
  ReopenSchedulerProjectRequest,
  SchedulerResetTimesheetMode,
  SchedulerApplyRescueRequestInput,
  SchedulerBoardResponse,
  UpdateSchedulerAutoProgressRequest,
  UpdateSchedulerCalendarRequestInput,
  SchedulerCompleteStageRequest,
  SchedulerPauseStageRequest,
  SchedulerStaffingRequestInput,
  SchedulerStartNowRequest,
  SchedulerWhatIfRequestInput,
  UpdateSchedulerProjectRequest,
  UpdateSchedulerTeamRequest,
  UpdateTeamManualHoursRequest,
  UpdateWorkSessionRequest,
  UpdateWorkerRequest,
  WorkerRosterResponse,
  CollaboratorEngagementsResponse,
  UpsertCollaboratorEngagementRequest,
  WorkerSessionsResponse,
} from '@kreart/shared';
import { operationsSchedulerApi } from './api';
import { errorKeyFor as schedulerErrorKeyFor } from './errorMessages';
import type { TimesheetPeriod } from './timesheet-period';

const ROOT = ['operations-scheduler'] as const;
export const operationsSchedulerQueryKeys = {
  all: ROOT,
  board: () => [...ROOT, 'board'] as const,
  roster: () => [...ROOT, 'roster'] as const,
  stats: () => [...ROOT, 'stats'] as const,
  preview: (body: PreviewSchedulerEnrollmentRequest | null) => [...ROOT, 'preview', body] as const,
  whatIf: (body: SchedulerWhatIfRequestInput | null) => [...ROOT, 'what-if', body] as const,
  sessions: (workerId: string, day?: string) => [...ROOT, 'sessions', workerId, day ?? ''] as const,
  timesheet: (period: TimesheetPeriod) => [...ROOT, 'timesheet', period.from, period.to] as const,
  projectHours: (period: TimesheetPeriod) =>
    [...ROOT, 'project-hours', period.from, period.to] as const,
  collaborators: () => [...ROOT, 'collaborators'] as const,
  history: (schedulerProjectId: string) => [...ROOT, 'history', schedulerProjectId] as const,
  // Invalidated wherever `history` is: both read the flow's tallies and the sessions.
  attendance: (schedulerProjectId: string) => [...ROOT, 'attendance', schedulerProjectId] as const,
};

/**
 * `enabled` is for consumers that only need the board while something is open
 * — without it they would keep the minute-by-minute poll below running on a
 * page that never shows the board.
 */
export function useSchedulerBoard(enabled = true) {
  return useQuery({
    queryKey: operationsSchedulerQueryKeys.board(),
    queryFn: () => operationsSchedulerApi.getBoard(),
    enabled,
    placeholderData: keepPreviousData,
    // Keep the board live: the notifications are time-relative (how overdue a
    // project is, deadline bands, projected dates), so re-fetch every minute and
    // on refocus so the figures climb as real time passes — not only on actions.
    //
    // …and every 3 s while a deadline recommendation is still being worked out:
    // the server budgets its searches per request and answers the rest PENDING,
    // so the next few reads are what fill them in. Back to the minute as soon as
    // none is pending.
    refetchInterval: (query) =>
      query.state.data?.alerts.some((alert) => alert.rescue?.status === 'PENDING') ? 3_000 : 60_000,
    refetchOnWindowFocus: true,
    meta: { silent: true },
  });
}

/**
 * Board-wide what-if projection ("what if one more team worked CUTTING?", "what
 * if this project came in next month?") for the Gantt's before/after lanes.
 * Cached briefly and kept while switching scenarios so toggling a preview never
 * blanks the chart; refreshed on the board's own minute so the two lanes do not
 * drift apart while someone reads them.
 */
export function useSchedulerWhatIf(body: SchedulerWhatIfRequestInput | null) {
  return useQuery({
    queryKey: operationsSchedulerQueryKeys.whatIf(body),
    queryFn: () => operationsSchedulerApi.whatIfPreview(body!),
    enabled: body !== null,
    staleTime: 30_000,
    refetchInterval: body !== null ? 60_000 : false,
    placeholderData: keepPreviousData,
    meta: { silent: true },
  });
}

/**
 * The Gantt simulation's "Recomandă echipele". A mutation rather than a query:
 * it is a search worth up to a couple of seconds, run when the manager asks
 * and read until they ask again — not refetched behind their back while they
 * compare its variants. Silent: the panel shows its own error line.
 */
export function useStaffingRecommendation() {
  return useMutation({
    mutationFn: (body: SchedulerStaffingRequestInput) =>
      operationsSchedulerApi.staffingRecommendation(body),
    meta: { silent: true },
  });
}

/**
 * Dry-run enrollment projection for the dialog. Callers debounce `body`;
 * silent because a transient 4xx while typing must not toast.
 */
export function useEnrollmentPreview(body: PreviewSchedulerEnrollmentRequest | null) {
  return useQuery({
    queryKey: operationsSchedulerQueryKeys.preview(body),
    queryFn: () => operationsSchedulerApi.previewEnrollment(body!),
    enabled: body !== null,
    placeholderData: keepPreviousData,
    meta: { silent: true },
  });
}

/**
 * Every scheduler mutation returns the fresh board; cache it directly.
 *
 * Board-button mutations are NOT `meta: { silent: true }`: the page renders no
 * inline error surface, so failures (e.g. a 409 when completing an
 * already-finished stage) fall through to the global MutationCache toast.
 * Dialog mutations pass `silent` — their FormErrorBanner reports inline.
 */
function useBoardMutation<TVariables>(
  mutationFn: (variables: TVariables) => Promise<SchedulerBoardResponse>,
  opts: {
    silent?: boolean;
    resolveError?: typeof schedulerErrorKeyFor;
    /** The action also moves timesheet entries, so every view of them goes stale. */
    timesheet?: boolean;
  } = {},
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn,
    ...(opts.silent
      ? { meta: { silent: true } }
      : opts.resolveError
        ? { meta: { resolveError: opts.resolveError } }
        : {}),
    onSuccess: (data: SchedulerBoardResponse) => {
      qc.setQueryData(operationsSchedulerQueryKeys.board(), data);
      // A board action changes the world every what-if was projected from;
      // left cached, the ghost lane would sit next to fresh bars and disagree.
      void qc.invalidateQueries({ queryKey: [...ROOT, 'what-if'] });
      // …and every warranty history: stages, statuses, whole cycles (a reopen
      // adds one, a cancel may delete one) are exactly what it lists.
      void qc.invalidateQueries({ queryKey: [...ROOT, 'history'] });
      void qc.invalidateQueries({ queryKey: [...ROOT, 'attendance'] });
      if (opts.timesheet) {
        void qc.invalidateQueries({ queryKey: [...ROOT, 'timesheet'] });
        void qc.invalidateQueries({ queryKey: [...ROOT, 'project-hours'] });
        void qc.invalidateQueries({ queryKey: [...ROOT, 'sessions'] });
        void qc.invalidateQueries({ queryKey: operationsSchedulerQueryKeys.roster() });
        void qc.invalidateQueries({ queryKey: operationsSchedulerQueryKeys.stats() });
      }
    },
  });
}

export function useEnrollSchedulerProject() {
  return useBoardMutation(
    (body: EnrollSchedulerProjectRequest) => operationsSchedulerApi.enrollProject(body),
    { silent: true },
  );
}

/**
 * Silent by default, for the edit dialog, whose banner words the refusal. The
 * board's own quick edits (the inline estimated start, "Pornește") have no
 * surface of their own and pass `{ silent: false }`: an intervention's start
 * past its warranty, or a row finished meanwhile, is refused there too, and a
 * silent refusal would only show as the old date quietly coming back.
 */
export function useUpdateSchedulerProject({ silent = true }: { silent?: boolean } = {}) {
  return useBoardMutation(
    ({
      schedulerProjectId,
      body,
    }: {
      schedulerProjectId: string;
      body: UpdateSchedulerProjectRequest;
    }) => operationsSchedulerApi.updateProject(schedulerProjectId, body),
    silent ? { silent: true } : { resolveError: schedulerErrorKeyFor },
  );
}

export function useRemoveSchedulerProject() {
  return useBoardMutation((schedulerProjectId: string) =>
    operationsSchedulerApi.removeProject(schedulerProjectId),
  );
}

/**
 * Silent: it runs from the edit dialog, whose banner words the refusal. No
 * `timesheet` leaves the choice to the server's default (taken off the project).
 */
export function useResetSchedulerProjectHours() {
  return useBoardMutation(
    ({
      schedulerProjectId,
      timesheet,
    }: {
      schedulerProjectId: string;
      timesheet?: SchedulerResetTimesheetMode;
    }) =>
      operationsSchedulerApi.resetProjectHours(schedulerProjectId, timesheet ? { timesheet } : {}),
    // Its entries may have been taken off the project or deleted.
    { silent: true, timesheet: true },
  );
}

/**
 * Put a finished project back into the flow as a warranty intervention. Silent:
 * the reopen dialog's banner words the refusals (expired, already open, a start
 * past the warranty's end) where the manager can still correct the form.
 */
export function useReopenSchedulerProject() {
  return useBoardMutation(
    ({
      schedulerProjectId,
      body,
    }: {
      schedulerProjectId: string;
      body: ReopenSchedulerProjectRequest;
    }) => operationsSchedulerApi.reopenProject(schedulerProjectId, body),
    { silent: true },
  );
}

/**
 * Cancel the intervention in progress. A board button with no dialog of its
 * own, so a refusal goes to the toast — worded by the scheduler, since "it was
 * already finished" is not the shared "conflict" message.
 */
export function useCancelSchedulerIntervention() {
  return useBoardMutation(
    (interventionId: string) => operationsSchedulerApi.cancelIntervention(interventionId),
    { resolveError: schedulerErrorKeyFor },
  );
}

/**
 * A project's production run and every warranty intervention on it, fetched
 * while its history is expanded. Worked minutes grow while a cycle runs, so it
 * polls on the board's own minute; silent, because the panel says inline when
 * it could not load.
 */
export function useSchedulerProjectHistory(schedulerProjectId: string | null) {
  return useQuery({
    queryKey: operationsSchedulerQueryKeys.history(schedulerProjectId ?? ''),
    // skipToken rather than `enabled`, as in useCompleteStagePreview: an
    // explicit refetch() would otherwise still fire for "no project".
    queryFn:
      schedulerProjectId === null
        ? skipToken
        : () => operationsSchedulerApi.getProjectHistory(schedulerProjectId),
    staleTime: 30_000,
    refetchInterval: schedulerProjectId !== null ? 60_000 : false,
    meta: { silent: true },
  });
}

/**
 * Who clocked what on each operation of one board row, beside the flow's own
 * tally — "Editează programarea" and Pontaj lunar's project pane read the same
 * answer. The flow's figure grows while an operation runs, so it polls on the
 * board's minute; silent, because both places say inline when it failed.
 */
export function useSchedulerProjectAttendance(schedulerProjectId: string | null) {
  return useQuery({
    queryKey: operationsSchedulerQueryKeys.attendance(schedulerProjectId ?? ''),
    queryFn:
      schedulerProjectId === null
        ? skipToken
        : () => operationsSchedulerApi.getProjectAttendance(schedulerProjectId),
    staleTime: 30_000,
    refetchInterval: schedulerProjectId !== null ? 60_000 : false,
    meta: { silent: true },
  });
}

export function useHoldSchedulerProject() {
  return useBoardMutation((schedulerProjectId: string) =>
    operationsSchedulerApi.holdProject(schedulerProjectId),
  );
}

export function useCompleteSchedulerStage() {
  return useBoardMutation(
    ({ stageId, body }: { stageId: string; body?: SchedulerCompleteStageRequest }) =>
      operationsSchedulerApi.completeStage(stageId, body),
    // A pick the board moved past has its own explanation. And "Salvează pontaj?"
    // may have put hours in the timesheet, so every view of it goes stale.
    { resolveError: schedulerErrorKeyFor, timesheet: true },
  );
}

/**
 * What "Done" on a running operation would lead to, fetched while its
 * confirmation is open. Silent: the dialog says inline when it could not load,
 * and "Done" itself still works without it.
 */
export function useCompleteStagePreview(stageId: string | null) {
  return useQuery({
    queryKey: [...ROOT, 'complete-preview', stageId] as const,
    // skipToken rather than `enabled`: an explicit refetch() ignores `enabled`
    // and would post a preview for a stage that is no longer there.
    queryFn:
      stageId === null ? skipToken : () => operationsSchedulerApi.previewCompleteStage(stageId),
    // Every open is a fresh look: crews change hands minute by minute.
    gcTime: 0,
    staleTime: 0,
    // …but only on open, and deliberately after a refused "Done". A re-read
    // while the manager is reading — a refocused tab — would reshuffle the
    // list under a pick they are about to confirm.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
    meta: { silent: true },
  });
}

/**
 * The total worked on the operation and what "Salvează pontaj?" offers, read while
 * the confirmation of "Done" is open. Silent, like the preview: "Done" works
 * without it.
 */
export function useCompleteStageTimesheet(stageId: string | null) {
  return useQuery({
    queryKey: [...ROOT, 'complete-timesheet', stageId] as const,
    queryFn:
      stageId === null ? skipToken : () => operationsSchedulerApi.getCompleteTimesheet(stageId),
    gcTime: 0,
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
    meta: { silent: true },
  });
}

/** What "Pune în așteptare" would free; read on each open, like the "Done" preview. */
export function usePauseStagePreview(stageId: string | null) {
  return useQuery({
    queryKey: [...ROOT, 'pause-preview', stageId] as const,
    queryFn: stageId === null ? skipToken : () => operationsSchedulerApi.previewPauseStage(stageId),
    gcTime: 0,
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
    meta: { silent: true },
  });
}

export function usePauseSchedulerStage() {
  return useBoardMutation(
    ({ stageId, body }: { stageId: string; body?: SchedulerPauseStageRequest }) =>
      operationsSchedulerApi.pauseStage(stageId, body),
  );
}

export function useResumeSchedulerStage() {
  return useBoardMutation((stageId: string) => operationsSchedulerApi.resumeStage(stageId));
}

/**
 * Dry-run for the urgent "Start now" action. Non-silent: a 409 ("no team at
 * this stage yet") must reach the user as a toast.
 */
export function useStartNowPreview() {
  return useMutation({
    mutationFn: (stageId: string) => operationsSchedulerApi.previewStartNow(stageId),
    // "No free crew" is a scheduler-specific refusal — surface its own message
    // instead of the shared "conflict" fallback.
    meta: { resolveError: schedulerErrorKeyFor },
  });
}

export function useStartNowSchedulerStage() {
  return useBoardMutation(
    ({ stageId, body }: { stageId: string; body?: SchedulerStartNowRequest }) =>
      operationsSchedulerApi.startNow(stageId, body),
    { resolveError: schedulerErrorKeyFor },
  );
}

export function useConfirmSchedulerProposal() {
  return useBoardMutation((proposalId: string) =>
    operationsSchedulerApi.confirmProposal(proposalId),
  );
}

export function useDismissSchedulerProposal() {
  return useBoardMutation((proposalId: string) =>
    operationsSchedulerApi.dismissProposal(proposalId),
  );
}

export function useDismissSchedulerAlert() {
  return useBoardMutation((alertId: string) => operationsSchedulerApi.dismissAlert(alertId));
}

/**
 * "Aplică" on a deadline recommendation. Silent: the recommendation dialog
 * words a refusal inline (the plan went stale, a name is taken) and refetches
 * the board so the manager is looking at the current plan when they retry.
 */
export function useApplyRescue() {
  return useBoardMutation(
    ({
      schedulerProjectId,
      body,
    }: {
      schedulerProjectId: string;
      body: SchedulerApplyRescueRequestInput;
    }) => operationsSchedulerApi.applyRescue(schedulerProjectId, body),
    { silent: true },
  );
}

/**
 * The × on a planned or reserved crew's chip. A board button with no dialog of
 * its own, so a refusal (the plan already consumed, the row gone) goes to the
 * toast, worded by the scheduler.
 */
export function useRemovePlannedTeam() {
  return useBoardMutation(
    ({ stageId, teamId }: { stageId: string; teamId: string }) =>
      operationsSchedulerApi.removePlannedTeam(stageId, teamId),
    { resolveError: schedulerErrorKeyFor },
  );
}

/**
 * Team mutations return the board, but the roster groups workers BY team — so
 * creating, renaming or deleting one changes "Pontaj lunar" too: its Echipe
 * pane creates teams, where a board-only refresh would leave the new team
 * invisible until the next poll, and its grid groups the month by team.
 */
function useTeamMutation<TVariables>(
  mutationFn: (variables: TVariables) => Promise<SchedulerBoardResponse>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn,
    // Team edits are refused for reasons only this feature can word — a team on
    // a stage, a team that still has members — so the global toast is given the
    // scheduler's resolver instead of the shared "conflict" fallback.
    meta: { resolveError: schedulerErrorKeyFor },
    onSuccess: (data: SchedulerBoardResponse) => {
      qc.setQueryData(operationsSchedulerQueryKeys.board(), data);
      void qc.invalidateQueries({ queryKey: operationsSchedulerQueryKeys.roster() });
      void qc.invalidateQueries({ queryKey: [...ROOT, 'timesheet'] });
      // Crews are exactly what a what-if distributes; see useBoardMutation.
      void qc.invalidateQueries({ queryKey: [...ROOT, 'what-if'] });
      // A history lists who worked each operation — and unassigning a team
      // changes that as much as a board action does.
      void qc.invalidateQueries({ queryKey: [...ROOT, 'history'] });
      void qc.invalidateQueries({ queryKey: [...ROOT, 'attendance'] });
    },
  });
}

export function useCreateSchedulerTeam() {
  return useTeamMutation((body: CreateSchedulerTeamRequest) =>
    operationsSchedulerApi.createTeam(body),
  );
}

export function useUpdateSchedulerTeam() {
  return useTeamMutation(({ teamId, body }: { teamId: string; body: UpdateSchedulerTeamRequest }) =>
    operationsSchedulerApi.updateTeam(teamId, body),
  );
}

/** Hand-entered corrections; the board carries them alongside the measured figures. */
export function useUpdateTeamManualHours() {
  return useTeamMutation(
    ({ teamId, body }: { teamId: string; body: UpdateTeamManualHoursRequest }) =>
      operationsSchedulerApi.updateTeamManualHours(teamId, body),
  );
}

/**
 * Zero a team's counters (measured + manual) — the reset in Pontaj lunar's "Raport ore".
 * Pass a team id for one row, undefined for the report-wide wipe.
 */
export function useResetTeamHours() {
  return useTeamMutation((teamId?: string) => operationsSchedulerApi.resetTeamHours(teamId));
}

export function useDeleteSchedulerTeam() {
  return useTeamMutation((teamId: string) => operationsSchedulerApi.deleteTeam(teamId));
}

/** Put another team on a running stage — it then progresses proportionally faster. */
export function useAssignStageTeam() {
  return useTeamMutation(({ stageId, teamId }: { stageId: string; teamId: string }) =>
    operationsSchedulerApi.assignStageTeam(stageId, { teamId }),
  );
}

export function useUnassignStageTeam() {
  return useTeamMutation(({ stageId, teamId }: { stageId: string; teamId: string }) =>
    operationsSchedulerApi.unassignStageTeam(stageId, teamId),
  );
}

/**
 * Clock mutations move attendance too — an advance carries open sessions across
 * the jump (fresh startedAt, credited minutes) and a reset re-anchors them to
 * real time — but they only return the board. Without invalidating the roster,
 * the roster keeps rendering the pre-jump session and its Stop button
 * 409s ("This change conflicts with the current state") against a session that
 * no longer exists. Worker/project stats are recomputed by the same carry.
 */
function useClockMutation<TVariables>(
  mutationFn: (variables: TVariables) => Promise<SchedulerBoardResponse>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (data: SchedulerBoardResponse) => {
      qc.setQueryData(operationsSchedulerQueryKeys.board(), data);
      void qc.invalidateQueries({ queryKey: operationsSchedulerQueryKeys.roster() });
      void qc.invalidateQueries({ queryKey: operationsSchedulerQueryKeys.stats() });
      // Moving the clock moves every projection a what-if is measured from.
      void qc.invalidateQueries({ queryKey: [...ROOT, 'what-if'] });
      // …and the history's worked minutes — and whether a warranty is still
      // running at all, since expiry is measured against this same clock.
      void qc.invalidateQueries({ queryKey: [...ROOT, 'history'] });
      void qc.invalidateQueries({ queryKey: [...ROOT, 'attendance'] });
    },
  });
}

export function useResetSchedulerClock() {
  return useClockMutation<void>(() => operationsSchedulerApi.resetClock());
}

/**
 * Save the shop's calendar ("Zile și program de lucru"). It moves every date on
 * the board — like the clock — and may rewrite workers' programs, which the
 * roster, the overtime of the days to come and the pay are read from.
 * Silent: the dialog reports a refusal inline.
 */
/**
 * "Fluxul numără singur orele" on or off. Every figure the flow derives from
 * an operation's hours moves with it — the board, the projections, the
 * project hours and the team counters — so all of them are read again.
 */
export function useUpdateAutoProgress() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateSchedulerAutoProgressRequest) =>
      operationsSchedulerApi.updateAutoProgress(body),
    meta: { silent: true },
    onSuccess: (data: SchedulerBoardResponse) => {
      qc.setQueryData(operationsSchedulerQueryKeys.board(), data);
      void qc.invalidateQueries({ queryKey: operationsSchedulerQueryKeys.roster() });
      void qc.invalidateQueries({ queryKey: operationsSchedulerQueryKeys.stats() });
      void qc.invalidateQueries({ queryKey: [...ROOT, 'what-if'] });
      void qc.invalidateQueries({ queryKey: [...ROOT, 'history'] });
      void qc.invalidateQueries({ queryKey: [...ROOT, 'attendance'] });
      void qc.invalidateQueries({ queryKey: [...ROOT, 'project-hours'] });
    },
  });
}

export function useUpdateSchedulerCalendar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateSchedulerCalendarRequestInput) =>
      operationsSchedulerApi.updateCalendar(body),
    meta: { silent: true },
    onSuccess: (data: SchedulerBoardResponse) => {
      qc.setQueryData(operationsSchedulerQueryKeys.board(), data);
      void qc.invalidateQueries({ queryKey: operationsSchedulerQueryKeys.roster() });
      void qc.invalidateQueries({ queryKey: operationsSchedulerQueryKeys.stats() });
      void qc.invalidateQueries({ queryKey: [...ROOT, 'what-if'] });
      void qc.invalidateQueries({ queryKey: [...ROOT, 'history'] });
      void qc.invalidateQueries({ queryKey: [...ROOT, 'attendance'] });
      void qc.invalidateQueries({ queryKey: [...ROOT, 'timesheet'] });
      // A program applied to salaried workers re-derives their rate, which
      // also prices any day they worked before they had one.
      void qc.invalidateQueries({ queryKey: [...ROOT, 'project-hours'] });
    },
  });
}

// --------------------------------------------------------------------------- //
// Workers & attendance (roster ledger, separate from the board)               //
// --------------------------------------------------------------------------- //

/** Roster for "Pontaj lunar"'s people panes; `enabled` keeps a dialog's copy idle while it is closed. */
export function useWorkerRoster(enabled = true) {
  return useQuery({
    queryKey: operationsSchedulerQueryKeys.roster(),
    queryFn: () => operationsSchedulerApi.getRoster(),
    enabled,
    placeholderData: keepPreviousData,
    refetchInterval: enabled ? 60_000 : false,
    refetchOnWindowFocus: true,
    meta: { silent: true },
  });
}

/**
 * Every roster mutation returns the fresh roster; cache it directly. The board
 * is invalidated too: staffing drives the team cards' worker badge and the
 * TEAM_UNSTAFFED notification, so adding, moving or deactivating a worker has to
 * clear (or raise) those without waiting for the next board poll.
 *
 * Leave and headcount are capacity now — a crew works at the speed of the
 * people present — so a roster change also moves every projection a what-if was
 * measured from, the deadline recommendations' previews included.
 */
function useRosterMutation<TVariables>(
  mutationFn: (variables: TVariables) => Promise<WorkerRosterResponse>,
  opts: { silent?: boolean } = {},
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn,
    ...(opts.silent ? { meta: { silent: true } } : {}),
    onSuccess: (data: WorkerRosterResponse) => {
      qc.setQueryData(operationsSchedulerQueryKeys.roster(), data);
      void qc.invalidateQueries({ queryKey: operationsSchedulerQueryKeys.board() });
      void qc.invalidateQueries({ queryKey: [...ROOT, 'what-if'] });
      // Stopping a session closes minutes the history counts as clocked.
      void qc.invalidateQueries({ queryKey: [...ROOT, 'history'] });
      void qc.invalidateQueries({ queryKey: [...ROOT, 'attendance'] });
      // The roster is edited on "Pontaj lunar" itself, next to the month it
      // feeds: a new, renamed, moved or re-rated worker and a stopped session
      // all change the grid, the pay, the project hours and the statistics.
      void qc.invalidateQueries({ queryKey: [...ROOT, 'timesheet'] });
      void qc.invalidateQueries({ queryKey: [...ROOT, 'project-hours'] });
      void qc.invalidateQueries({ queryKey: operationsSchedulerQueryKeys.stats() });
    },
  });
}

export function useCreateWorker() {
  return useRosterMutation(
    ({ teamId, body }: { teamId: string; body: CreateWorkerRequest }) =>
      operationsSchedulerApi.createWorker(teamId, body),
    { silent: true },
  );
}

export function useUpdateWorker() {
  return useRosterMutation(
    ({ workerId, body }: { workerId: string; body: UpdateWorkerRequest }) =>
      operationsSchedulerApi.updateWorker(workerId, body),
    { silent: true },
  );
}

/** Silent: the first (unconfirmed) call 409s by design and the dialog owns the copy. */
export function useDeleteWorker() {
  return useRosterMutation(
    ({ workerId, purgeHistory }: { workerId: string; purgeHistory: boolean }) =>
      operationsSchedulerApi.deleteWorker(workerId, purgeHistory),
    { silent: true },
  );
}

/** Silent: the leave dialog words a refusal (an overlap, a bad range) inline. */
export function useCreateWorkerLeave() {
  return useRosterMutation(
    ({ workerId, body }: { workerId: string; body: CreateWorkerLeaveRequestInput }) =>
      operationsSchedulerApi.createWorkerLeave(workerId, body),
    { silent: true },
  );
}

/** Confirmed in its own dialog, so a refusal goes to the toast. */
export function useDeleteWorkerLeave() {
  return useRosterMutation((leaveId: string) => operationsSchedulerApi.deleteWorkerLeave(leaveId));
}

export function useStartWorkerSession() {
  return useRosterMutation((workerId: string) => operationsSchedulerApi.startSession(workerId));
}

export function useStopWorkerSession() {
  return useRosterMutation((workerId: string) => operationsSchedulerApi.stopSession(workerId));
}

export function useStartAllForTeam() {
  return useRosterMutation((teamId: string) => operationsSchedulerApi.startAllForTeam(teamId));
}

export function useStopAllForTeam() {
  return useRosterMutation((teamId: string) => operationsSchedulerApi.stopAllForTeam(teamId));
}

export function useWorkerStats(workerId: string | null, year: number) {
  return useQuery({
    queryKey: [...operationsSchedulerQueryKeys.stats(), 'worker', workerId, year],
    queryFn: () => operationsSchedulerApi.getWorkerStats(workerId as string, year),
    enabled: workerId !== null,
    placeholderData: keepPreviousData,
    meta: { silent: true },
  });
}

export function useProjectStats(projectId: string | null, year: number) {
  return useQuery({
    queryKey: [...operationsSchedulerQueryKeys.stats(), 'project', projectId, year],
    queryFn: () => operationsSchedulerApi.getProjectStats(projectId as string, year),
    enabled: projectId !== null,
    placeholderData: keepPreviousData,
    meta: { silent: true },
  });
}

/** A worker's raw clock in/out records, for hand corrections. */
export function useWorkerSessions(workerId: string | null, day?: string) {
  return useQuery({
    queryKey: operationsSchedulerQueryKeys.sessions(workerId ?? '', day),
    queryFn: () => operationsSchedulerApi.getWorkerSessions(workerId!, day),
    enabled: workerId !== null,
  });
}

/**
 * Workers x days grid over a period. `enabled` lets "Pontaj lunar" skip it while
 * only its people panes (employees, teams, …) have been opened.
 */
export function useTimesheet(period: TimesheetPeriod, enabled = true) {
  return useQuery({
    queryKey: operationsSchedulerQueryKeys.timesheet(period),
    queryFn: () => operationsSchedulerApi.getTimesheet(period.from, period.to),
    enabled,
    placeholderData: keepPreviousData,
  });
}

/**
 * One day of the timesheet — what each person already has on it, for the entry
 * form's day bars. The same cache entry as the timesheet's own one-day view, so
 * every entry saved moves both. Silent: the form simply shows nothing known yet.
 */
export function useTimesheetDay(date: string | null) {
  return useQuery({
    queryKey: date
      ? operationsSchedulerQueryKeys.timesheet({ from: date, to: date })
      : [...ROOT, 'timesheet', 'none'],
    queryFn: date === null ? skipToken : () => operationsSchedulerApi.getTimesheet(date, date),
    meta: { silent: true },
  });
}

/** What a period's hours were spent on, per project — the same rows the grid shows. */
export function useProjectHours(period: TimesheetPeriod, enabled = true) {
  return useQuery({
    queryKey: operationsSchedulerQueryKeys.projectHours(period),
    queryFn: () => operationsSchedulerApi.getProjectHours(period.from, period.to),
    enabled,
    placeholderData: keepPreviousData,
  });
}

/**
 * Session writes return the worker's refreshed session list; the roster totals
 * and the stats derive from the same rows, so both are invalidated as well.
 * Silent — the dialog reports failures inline (overlaps, inverted ranges).
 */
function useSessionMutation<TVariables>(
  mutationFn: (variables: TVariables) => Promise<WorkerSessionsResponse>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn,
    meta: { silent: true },
    onSuccess: (data: WorkerSessionsResponse) => {
      // The response is the worker's FULL list, so it cannot be written into a
      // day-scoped cache key — invalidate instead and let each view refetch its
      // own window. The grid and roster totals derive from the same rows.
      void qc.invalidateQueries({ queryKey: [...ROOT, 'sessions', data.workerId] });
      void qc.invalidateQueries({ queryKey: operationsSchedulerQueryKeys.roster() });
      void qc.invalidateQueries({ queryKey: operationsSchedulerQueryKeys.stats() });
      void qc.invalidateQueries({ queryKey: [...ROOT, 'timesheet'] });
      // Correcting a session can move hours between projects, so the project
      // report is stale for the same reason the grid is.
      void qc.invalidateQueries({ queryKey: [...ROOT, 'project-hours'] });
      // …and between a production run and an intervention, whose histories
      // each total the hours clocked on them.
      void qc.invalidateQueries({ queryKey: [...ROOT, 'history'] });
      void qc.invalidateQueries({ queryKey: [...ROOT, 'attendance'] });
      // A hand-entered entry counts on its project's operation in the flow, so
      // the board's tallies and finish dates move with it.
      void qc.invalidateQueries({ queryKey: operationsSchedulerQueryKeys.board() });
    },
  });
}

export function useCreateWorkSession() {
  return useSessionMutation(
    ({ workerId, body }: { workerId: string; body: CreateWorkSessionRequest }) =>
      operationsSchedulerApi.createWorkSession(workerId, body),
  );
}

/**
 * The same hours for several people at once. Invalidates what a single entry
 * does, for everyone it saved. Silent: the dialog words a refusal inline.
 */
export function useCreateWorkSessions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateWorkSessionsRequest) =>
      operationsSchedulerApi.createWorkSessions(body),
    meta: { silent: true },
    onSuccess: (data: CreateWorkSessionsResponse) => {
      for (const workerId of new Set(data.sessions.map((session) => session.workerId))) {
        void qc.invalidateQueries({ queryKey: [...ROOT, 'sessions', workerId] });
      }
      void qc.invalidateQueries({ queryKey: operationsSchedulerQueryKeys.roster() });
      void qc.invalidateQueries({ queryKey: operationsSchedulerQueryKeys.stats() });
      void qc.invalidateQueries({ queryKey: [...ROOT, 'timesheet'] });
      void qc.invalidateQueries({ queryKey: [...ROOT, 'project-hours'] });
      void qc.invalidateQueries({ queryKey: [...ROOT, 'history'] });
      void qc.invalidateQueries({ queryKey: [...ROOT, 'attendance'] });
      void qc.invalidateQueries({ queryKey: operationsSchedulerQueryKeys.board() });
    },
  });
}

export function useUpdateWorkSession() {
  return useSessionMutation(
    ({ sessionId, body }: { sessionId: string; body: UpdateWorkSessionRequest }) =>
      operationsSchedulerApi.updateWorkSession(sessionId, body),
  );
}

export function useDeleteWorkSession() {
  return useSessionMutation((sessionId: string) =>
    operationsSchedulerApi.deleteWorkSession(sessionId),
  );
}

/** Collaborator engagements; `days` arrives derived from the board. */
export function useCollaboratorEngagements(enabled = true) {
  return useQuery({
    queryKey: operationsSchedulerQueryKeys.collaborators(),
    queryFn: () => operationsSchedulerApi.getCollaboratorEngagements(),
    enabled,
  });
}

function useCollaboratorMutation<TVariables>(
  mutationFn: (variables: TVariables) => Promise<CollaboratorEngagementsResponse>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn,
    meta: { silent: true },
    onSuccess: (data: CollaboratorEngagementsResponse) => {
      qc.setQueryData(operationsSchedulerQueryKeys.collaborators(), data);
    },
  });
}

export function useUpsertCollaboratorEngagement() {
  return useCollaboratorMutation((body: UpsertCollaboratorEngagementRequest) =>
    operationsSchedulerApi.upsertCollaboratorEngagement(body),
  );
}

export function useDeleteCollaboratorEngagement() {
  return useCollaboratorMutation((engagementId: string) =>
    operationsSchedulerApi.deleteCollaboratorEngagement(engagementId),
  );
}
